import { useEffect, useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import RenderIf from "common/components/RenderIf";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FormField from "common/components/ui/FormField";
import ProfileImage from "common/components/ui/ProfileImage";
import SelectButton from "common/components/ui/SelectButton";

import { useCampaignContext } from "../../../../context/CampaignContext";
import useLinkedInEvents from "../../../../datahooks/useLinkedInEvents";
import useLinkedInGroups from "../../../../datahooks/useLinkedInGroups";
import MinerAccountSelector from "../MinerAccountSelector";

import type { LinkedInEvent, LinkedInGroup } from "../../../../types";

import EventCampaignImage from "assets/images/event-campaign.png";
import GroupCampaignImage from "assets/images/group-campaign.png";
import GroupDefaultImage from "assets/images/group-fallback.svg";

const selectOptions: Record<
  "group" | "event",
  {
    noOptionsText: string;
    name: string;
    label: string;
    imageSrc: string;
    infoText: string;
  }
> = {
  group: {
    imageSrc: GroupCampaignImage,
    name: "Groups",
    label: "Select a group",
    noOptionsText: "You are not a member of any LinkedIn Group",
    infoText:
      "Select a LinkedIn group you want to target with this campaign. Aimfox will automatically engage with other group members",
  },
  event: {
    imageSrc: EventCampaignImage,
    name: "Events",
    label: "Select an event",
    noOptionsText: "You are not a attending any LinkedIn Event",
    infoText:
      "Select a LinkedIn event you want to target with this campaign. Aimfox will engage with users attending this event",
  },
};

interface GroupAndEventSelectProps {
  type: "group" | "event";
  selectGroupOrEvent: (groupOrEvent: LinkedInGroup | LinkedInEvent) => void;
  initialId: string | undefined;
}

interface FilteredOptionsProps {
  options: LinkedInEvent[] | LinkedInGroup[];
  inputValue: string;
  selectGroupOrEvent: (option: LinkedInEvent | LinkedInGroup) => void;
}

function FilteredOptions({
  options,
  inputValue,
  selectGroupOrEvent,
}: FilteredOptionsProps) {
  const lowercasedInput = inputValue.toLowerCase();
  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(lowercasedInput),
  );

  if (!inputValue || filteredOptions.length) {
    return filteredOptions.map((option) => {
      const { name, images, id } = option;
      const imageUrl = images.length ? images[0].url : GroupDefaultImage;

      let description: string;

      if ("public" in option) {
        description = option.public ? "Public group" : "Private group";
      } else {
        description = option.description;
      }
      return (
        <DropdownMenuItem key={id} onSelect={() => selectGroupOrEvent(option)}>
          <ProfileImage src={imageUrl} size="sm" />
          <div>
            <h5 className="line-clamp-1 text-left text-sm-medium">{name}</h5>
            <p className="line-clamp-1 text-left text-xs text-black-500">
              {description}
            </p>
          </div>
        </DropdownMenuItem>
      );
    });
  }

  return (
    <p className="break-words p-4 text-center text-black-500">
      No results found for&nbsp;
      <span className="text-black-950">{inputValue}</span>
    </p>
  );
}

export default function GroupAndEventSelect({
  type,
  selectGroupOrEvent,
  initialId,
}: GroupAndEventSelectProps) {
  const { outreach_type: outreachType } = useCampaignContext();
  const isDrip = outreachType === "drip";

  const [inputValue, setInputValue] = useState("");
  const [isDuplicateError, setIsDuplicateError] = useState(false);
  const { linkedInGroups, isLoadingLinkedInGroups, refetchLinkedInGroups } =
    useLinkedInGroups({
      disableQuery: type === "event",
    });
  const { linkedInEvents, isLoadingLinkedInEvents, refetchLinkedInEvents } =
    useLinkedInEvents({
      disableQuery: type === "group",
    });

  useEffect(() => {
    if (initialId) {
      let existingEventOrGroup: LinkedInEvent | LinkedInGroup;
      if (type === "event" && linkedInEvents) {
        existingEventOrGroup = linkedInEvents.find(
          ({ id }) => id === initialId,
        );
      } else if (type === "group" && linkedInGroups) {
        existingEventOrGroup = linkedInGroups.find(
          ({ id }) => id === initialId,
        );
      }
      if (existingEventOrGroup) {
        selectGroupOrEvent(existingEventOrGroup);
      } else {
        // when duplicating a campaign, this error is shown if the the event or group is not found
        setIsDuplicateError(true);
      }
    }
  }, [linkedInGroups, linkedInEvents]);

  const options = (type === "group" ? linkedInGroups : linkedInEvents) || [];

  const isLoading =
    type === "group" ? isLoadingLinkedInGroups : isLoadingLinkedInEvents;

  const { label, name, imageSrc, infoText, noOptionsText } =
    selectOptions[type];

  function onMinerUpdate() {
    // We need to refetch the groups/events when miner updated on the BE
    if (type === "event") {
      refetchLinkedInEvents();
    } else {
      refetchLinkedInGroups();
    }
  }

  return (
    <div className="mb-20 lg:rounded-5xl lg:border lg:border-black-200 lg:p-5 lg:shadow-md">
      <img
        src={imageSrc}
        width={848}
        height={210}
        alt={`${name} Campaign`}
        className="mb-5 rounded-2xl"
      />

      <div className="flex flex-col gap-3 lg:flex-row lg:gap-2.5">
        <RenderIf condition={!isDrip}>
          <FormField label="Search account" htmlFor="miner-account-selector">
            <MinerAccountSelector
              className="w-40"
              onUpdate={onMinerUpdate}
              align="start"
            />
          </FormField>
        </RenderIf>

        <FormField
          className="w-full"
          label={label}
          hint={infoText}
          htmlFor=""
          error={
            isDuplicateError &&
            `We could not find this ${type} with this account. Please select a different ${type}`
          }
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={isLoading}>
              <SelectButton hasSelection={false} isLoading={isLoading}>
                {isLoading
                  ? `Loading ${name}...`
                  : `${label} for your targeting`}
              </SelectButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[--radix-popper-anchor-width]">
              {options.length > 2 && (
                <MenuSearchInput
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              )}
              <DropdownMenuList>
                {options.length ? (
                  <FilteredOptions
                    options={options}
                    inputValue={inputValue}
                    selectGroupOrEvent={(groupOrEvent) => {
                      selectGroupOrEvent(groupOrEvent);
                      setIsDuplicateError(false);
                    }}
                  />
                ) : (
                  <p className="p-4 text-center text-black-500">
                    {noOptionsText}
                  </p>
                )}
              </DropdownMenuList>
            </DropdownMenuContent>
          </DropdownMenu>
        </FormField>
      </div>
    </div>
  );
}
